// Purpose: landing — the authenticated user's home. Surfaces quick
// links into the two primary flows (Shares, Dropboxes) and a short
// value-prop statement.

import { Link } from 'react-router-dom';
import { ArrowRightIcon, FileTextIcon, UploadIcon } from '../components/icons/index.tsx';
import { Card } from '../components/ui/index.ts';

export function Landing() {
  return (
    <div className="flex flex-col gap-10">
      <header className="flex flex-col gap-3 max-w-3xl">
        <span className="text-label uppercase text-accent-primary">Welcome</span>
        <h1 className="font-display text-display-l text-fg-primary [font-feature-settings:var(--font-feature-display)]">
          Move files like the mission depends on it.
        </h1>
        <p className="text-body-l text-fg-secondary">
          End-to-end encrypted transfers with hash-chained audit. Pick the flow that fits the
          handoff.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LandingCard
          to="/shares"
          icon={<UploadIcon size={18} />}
          title="Shares"
          description="Send a package to an identified recipient with an out-of-band access code and a short TTL."
        />
        <LandingCard
          to="/dropboxes"
          icon={<FileTextIcon size={18} />}
          title="Dropboxes"
          description="Invite a contact to upload into a locked container — ephemeral or persistent, your call."
        />
      </div>
    </div>
  );
}

function LandingCard({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link to={to} className="block no-underline text-inherit group">
      <Card interactive padding="comfortable" className="flex items-start gap-4">
        <span
          aria-hidden
          className="flex items-center justify-center h-10 w-10 rounded-md bg-accent-muted text-accent-primary flex-shrink-0"
        >
          {icon}
        </span>
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <h2 className="text-heading-m text-fg-primary flex items-center gap-2">
            {title}
            <ArrowRightIcon
              size={14}
              className="text-fg-muted transition-transform duration-base ease-out group-hover:translate-x-1"
            />
          </h2>
          <p className="text-body-m text-fg-secondary">{description}</p>
        </div>
      </Card>
    </Link>
  );
}
